// Antwort aus /api/ai/chat in Panel-Nachrichtenformat überführen.

import { getContextualQuickReplies } from "./flows/flow.areas.js";

/**
 * @param {object} data
 * @param {{
 *   t: (key: string, params?: object) => string,
 *   pageContext?: object,
 *   lastUserText?: string,
 *   buildSuggestions?: (args: object) => Array<object>,
 *   sanitizeAssistantTextWhenChips?: (text: string) => string,
 * }} ctx
 */
export function buildAssistantFromChatResponse(data, ctx) {
  const {
    t,
    pageContext,
    lastUserText,
    buildSuggestions,
    sanitizeAssistantTextWhenChips,
  } = ctx;
  const meta = data?.trainingMeta;

  let content = String(data?.message?.content || "");
  // Nur Navigation an den Client – keine Tool-Dumps („Aktion ausgeführt“) in der UI.
  let toolCalls =
    Array.isArray(data?.toolCalls) && data.toolCalls.length > 0
      ? data.toolCalls.filter(
          (tc) => tc?.name === "ui.navigate" || tc?.name === "navigation.goTo",
        )
      : undefined;
  if (toolCalls && toolCalls.length === 0) toolCalls = undefined;

  /** @type {Record<string, unknown>} */
  const assistantMsg = {
    id: crypto.randomUUID(),
    role: "assistant",
    content,
    toolCalls,
    createdSession: data?.createdSession ?? undefined,
  };

  if (data?.askInvoicePersonName) {
    assistantMsg.content = t("ai.coachdesk.askInvoicePersonName");
    assistantMsg.kind = "coachdesk-ask";
    assistantMsg.coachDeskTopic = "invoices";
    assistantMsg.coachDeskScopes = data.coachDeskScopes || ["invoices"];
    assistantMsg.toolCalls = undefined;
  }

  if (data?.askPaymentKnowsName) {
    assistantMsg.content = t("ai.coachdesk.askPaymentKnowsName");
    assistantMsg.kind = "coachdesk-ask-payment-knows";
    assistantMsg.coachDeskTopic = "payments";
    assistantMsg.coachDeskScopes = data.coachDeskScopes || ["invoices"];
    assistantMsg.toolCalls = undefined;
  }

  if (data?.askPaymentPersonName) {
    assistantMsg.content = t("ai.coachdesk.askPaymentPersonName");
    assistantMsg.kind = "coachdesk-ask";
    assistantMsg.coachDeskTopic = "payments";
    assistantMsg.coachDeskScopes = data.coachDeskScopes || ["invoices"];
    assistantMsg.toolCalls = undefined;
  }

  if (data?.askTrainingPlayerName) {
    assistantMsg.content = t("ai.training.askPlayerName");
    assistantMsg.kind = "training";
    assistantMsg.toolCalls = undefined;
  }

  if (data?.coachDeskInvoices) {
    assistantMsg.kind = "coachdesk-invoices";
    assistantMsg.coachDeskTopic = "invoices";
  }

  if (data?.helpSearch) {
    assistantMsg.kind = "help";
    if (typeof data.helpHref === "string" && data.helpHref.trim()) {
      assistantMsg.helpHref = data.helpHref.trim();
    }
  }

  if (meta?.needsConfirm && meta.suggestedPerson) {
    assistantMsg.pendingTrainingConfirm = {
      personId: String(meta.suggestedPerson.id || ""),
      playerName: String(meta.suggestedPerson.name || ""),
    };
    assistantMsg.kind = "training";
    assistantMsg.toolCalls = undefined;
  } else if (
    meta?.ambiguous &&
    Array.isArray(meta.people) &&
    meta.people.length
  ) {
    assistantMsg.ambiguousPeople = meta.people;
    assistantMsg.kind = "training";
    assistantMsg.toolCalls = undefined;
  }

  const contextual = getContextualQuickReplies(assistantMsg);
  if (contextual.length) assistantMsg.quickReplies = contextual;

  if (
    typeof buildSuggestions === "function" &&
    typeof sanitizeAssistantTextWhenChips === "function"
  ) {
    const suggestions = buildSuggestions({
      chatMode: "general",
      pageContext,
      lastUserText,
      flowActive: false,
    });
    if (suggestions.length > 0 && !assistantMsg.quickReplies?.length) {
      assistantMsg.content = sanitizeAssistantTextWhenChips(
        String(assistantMsg.content),
      );
      assistantMsg.suggestions = suggestions;
    }
  }

  return assistantMsg;
}

/**
 * @param {Array<{ role?: string, content?: string, kind?: string, coachDeskTopic?: string, coachDeskScopes?: string[], pendingTrainingConfirm?: object }>} messages
 */
export function serializeChatMessagesForRemote(messages) {
  return (Array.isArray(messages) ? messages : []).map((m) => ({
    role: m.role === "assistant" ? "assistant" : "user",
    content: String(m.content || ""),
    ...(m.kind ? { kind: m.kind } : {}),
    ...(m.coachDeskTopic ? { coachDeskTopic: m.coachDeskTopic } : {}),
    ...(Array.isArray(m.coachDeskScopes)
      ? { coachDeskScopes: m.coachDeskScopes }
      : {}),
    ...(m.pendingTrainingConfirm
      ? { pendingTrainingConfirm: m.pendingTrainingConfirm }
      : {}),
    ...(m.createdSession?.id
      ? { createdSession: { id: String(m.createdSession.id) } }
      : {}),
  }));
}
